import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, MessageCircleQuestion, Flag, CreditCard, MessageSquare, ChevronRight, CheckCircle, CheckCheck, ShoppingBag, CalendarClock, Clock, Star, PackageMinus, Sparkles, Share2 } from 'lucide-react';
import { api } from '../services/api';

const ITEMS = [
  { key: 'pending_questions', label: 'Question(s) produit à répondre', icon: MessageCircleQuestion, tab: 'questions', cls: 'text-amber-600 bg-amber-50' },
  { key: 'reported_reviews', label: 'Avis signalé(s) à vérifier', icon: Flag, tab: 'reviews', cls: 'text-red-600 bg-red-50' },
  { key: 'pending_plates', label: 'Plaque(s) à valider', icon: CreditCard, tab: 'plates', cls: 'text-blue-600 bg-blue-50' },
  { key: 'new_contacts', label: 'Nouveau(x) message(s) client', icon: MessageSquare, tab: 'contact', cls: 'text-purple-600 bg-purple-50' },
];
export const ALERT_KIND = {
  order: { label: 'Commande', icon: ShoppingBag, cls: 'text-emerald-700 bg-emerald-50' },
  contact: { label: 'Message', icon: MessageSquare, cls: 'text-purple-600 bg-purple-50' },
  fitting: { label: 'Montage', icon: CalendarClock, cls: 'text-amber-700 bg-amber-50' },
  cron: { label: 'Tâche planifiée', icon: Clock, cls: 'text-red-600 bg-red-50' },
  review: { label: 'Avis', icon: Star, cls: 'text-orange-600 bg-orange-50' },
  stock: { label: 'Stock bas', icon: PackageMinus, cls: 'text-rose-700 bg-rose-50' },
  seo: { label: 'SEO IA', icon: Sparkles, cls: 'text-violet-700 bg-violet-50' },
  email: { label: 'E-mails', icon: MessageSquare, cls: 'text-red-700 bg-red-50' },
  share_viral: { label: 'Partage viral', icon: Share2, cls: 'text-teal-700 bg-teal-50' },
};
// Opens the admin tab of an alert; alerts carrying a deep link (e.g. supplier reorder) also set the URL params read by the target tab
export const goToAlert = (a, onNavigate) => {
  if (a.url && a.url.includes('?')) window.history.replaceState(null, '', a.url);
  onNavigate(a.tab || 'crons');
};
export const timeAgo = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000));
  return m < 1 ? 'à l\'instant' : m < 60 ? `il y a ${m} min` : m < 1440 ? `il y a ${Math.round(m / 60)} h` : `il y a ${Math.round(m / 1440)} j`;
};

// Admin bell: "to do" counters (from stats) + alert history (admin_notifications) with read / unread
const AdminNotificationsBell = ({ stats, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState({ items: [], unread: 0 });
  const ref = useRef(null);
  const items = ITEMS.map(i => ({ ...i, count: stats?.[i.key] || 0 })).filter(i => i.count > 0);
  const total = items.reduce((n, i) => n + i.count, 0) + (alerts.unread || 0);

  const load = useCallback(() => api.get('/admin/alerts', { params: { limit: 8 } }).then((r) => setAlerts(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const openAlert = async (a) => { setOpen(false); if (!a.read) await api.put(`/admin/alerts/${a.id}`, { read: true }).catch(() => {}); load(); goToAlert(a, onNavigate); };
  const toggleRead = async (e, a) => { e.stopPropagation(); await api.put(`/admin/alerts/${a.id}`, { read: !a.read }).catch(() => {}); load(); };
  const readAll = async () => { await api.post('/admin/alerts/read-all').catch(() => {}); load(); };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)} data-testid="admin-bell" aria-label="Notifications" className="relative p-2 text-gray-500 hover:text-gray-700">
        <Bell size={20} />
        {total > 0 && <span data-testid="admin-bell-dot" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{total > 99 ? '99+' : total}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden" data-testid="admin-bell-menu">
          <p className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b bg-gray-50">À traiter</p>
          {items.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2" data-testid="admin-bell-empty"><CheckCircle size={16} className="text-green-600" /> Rien en attente, tout est à jour.</p>
          ) : (
            <ul className="divide-y">
              {items.map(i => (
                <li key={i.key}>
                  <button type="button" onClick={() => { setOpen(false); onNavigate(i.tab); }} data-testid={`admin-bell-item-${i.key}`} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-orange-50 transition-colors text-left">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${i.cls}`}><i.icon size={16} /></span>
                    <span className="flex-1"><strong>{i.count}</strong> {i.label}</span>
                    <ChevronRight size={16} className="text-gray-400" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide border-y bg-gray-50 flex items-center justify-between">
            <span>Alertes {alerts.unread > 0 && <span className="ml-1 text-red-600" data-testid="admin-alerts-unread">({alerts.unread} non lue{alerts.unread > 1 ? 's' : ''})</span>}</span>
            <button type="button" onClick={readAll} disabled={!alerts.unread} className="normal-case font-medium text-orange-600 disabled:text-gray-400 inline-flex items-center gap-1" data-testid="admin-alerts-read-all"><CheckCheck size={13} /> Tout marquer lu</button>
          </div>
          {alerts.items.length === 0 ? <p className="px-4 py-4 text-sm text-gray-400" data-testid="admin-alerts-empty">Aucune alerte pour le moment.</p> : (
            <ul className="divide-y max-h-80 overflow-y-auto" data-testid="admin-alerts-list">
              {alerts.items.map((a) => { const k = ALERT_KIND[a.kind] || ALERT_KIND.cron; return (
                <li key={a.id} className={`flex items-start gap-3 px-4 py-2.5 text-sm hover:bg-orange-50 cursor-pointer ${a.read ? '' : 'bg-orange-50/50'}`} onClick={() => openAlert(a)} data-testid={`admin-alert-${a.id}`}>
                  <span className={`mt-0.5 w-8 h-8 shrink-0 rounded-lg flex items-center justify-center ${k.cls}`}><k.icon size={15} /></span>
                  <span className="flex-1 min-w-0">
                    <span className={`block truncate ${a.read ? 'text-gray-700' : 'font-semibold text-gray-900'}`}>{a.title}</span>
                    <span className="block text-xs text-gray-500 line-clamp-2">{a.body}</span>
                    <span className="block text-[11px] text-gray-400 mt-0.5">{timeAgo(a.created_at)}</span>
                  </span>
                  <button type="button" onClick={(e) => toggleRead(e, a)} title={a.read ? 'Marquer non lu' : 'Marquer lu'} className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${a.read ? 'border border-gray-300' : 'bg-orange-500'}`} data-testid={`admin-alert-toggle-${a.id}`} />
                </li>
              ); })}
            </ul>
          )}
          <button type="button" onClick={() => { setOpen(false); onNavigate('crons'); }} className="w-full text-center text-xs text-orange-600 font-medium py-2 border-t hover:bg-gray-50" data-testid="admin-alerts-history">Historique des alertes</button>
        </div>
      )}
    </div>
  );
};

export default AdminNotificationsBell;
